import React from 'react';
import { formatValue, formatShort, formatRange } from './format';

// The left→right journey a metric is on: where you started, where you are now,
// the next reachable step, and your target range — marks laid out by distance
// to the target. Lifted from the TargetsEditor row (the visual Curtis liked)
// so the home Dials and the target editor read the same. Direction-aware.

// Position on the track: 0 = furthest from target, 1 = at the target edge.
const trackPos = (v, worst, edge) => {
  if (v === null || v === undefined || edge === null || worst === edge) return 1;
  return Math.min(1, Math.max(0, 1 - Math.abs(v - edge) / Math.abs(worst - edge)));
};

// Nudge label positions apart so words never overlap (dots keep their true pos).
const spreadLabels = (positions) => {
  const MIN = 0.18;
  const out = [...positions];
  for (let i = 1; i < out.length; i++) out[i] = Math.max(out[i], out[i - 1] + MIN);
  if (out[out.length - 1] > 1) {
    out[out.length - 1] = 1;
    for (let i = out.length - 2; i >= 0; i--) out[i] = Math.min(out[i], out[i + 1] - MIN);
  }
  return out;
};

const dotLeft = (p) => `${(2 + p * 95).toFixed(1)}%`;

const inBandOf = (now, low, high, direction) => {
  if (now === null || now === undefined) return false;
  if (direction === 'up') return low != null && now >= low;
  if (direction === 'down') return high != null && now <= high;
  return low != null && high != null && now >= low && now <= high;
};

// The band edge the value is walking toward (null when in band / no band).
const edgeOf = (now, low, high, direction) => {
  if (now === null || now === undefined) return null;
  if (direction === 'up') return low != null && now < low ? low : null;
  if (direction === 'down') return high != null && now > high ? high : null;
  if (low != null && now < low) return low;
  if (high != null && now > high) return high;
  return null;
};

// Two hue identities: gold = you (now), green = the healthier direction (next, target).
const markStyle = {
  start: 'w-2.5 h-2.5 rounded-full border-2 border-gray-400 dark:border-gray-500 bg-white dark:bg-surface-dark-card',
  now: 'w-3.5 h-3.5 rounded-full bg-primary-500 solar-glow',
  next: 'w-3 h-3 rounded-full bg-good',
  target: 'w-1.5 h-4 rounded-sm bg-good-strong dark:bg-good',
};
const labelStyle = {
  start: 'text-gray-600 dark:text-gray-300',
  now: 'text-primary-700 dark:text-primary-400 font-semibold',
  next: 'text-good-strong dark:text-good font-medium',
  target: 'text-good-strong dark:text-good font-semibold',
};

const JourneyTrack = ({ start, now, low, high, next, nextWeeks, direction, unit }) => {
  const inBand = inBandOf(now, low, high, direction);
  const edge = edgeOf(now, low, high, direction);
  const targetText = formatRange(low, high, unit);
  const hasNext =
    !inBand && next !== null && next !== undefined && now !== null && next !== now;

  let marks = null;
  if (!inBand && edge !== null && now !== null && now !== undefined) {
    const candidates = start !== null && start !== undefined ? [start, now] : [now];
    const worst = candidates.reduce((a, b) => (Math.abs(a - edge) >= Math.abs(b - edge) ? a : b));
    marks = [];
    const posNow = trackPos(now, worst, edge);
    const posStart = start !== null && start !== undefined ? trackPos(start, worst, edge) : null;
    if (posStart !== null && Math.abs(posStart - posNow) >= 0.05) {
      marks.push({ key: 'start', pos: posStart, label: 'start', title: `Started: ${formatValue(start, unit)}` });
    }
    marks.push({ key: 'now', pos: posNow, label: 'now', title: `Now: ${formatValue(now, unit)}` });
    if (hasNext) {
      marks.push({ key: 'next', pos: trackPos(next, worst, edge), label: 'next', title: `Next step: ${formatValue(next, unit)}` });
    }
    marks.push({ key: 'target', pos: 1, label: 'target', title: `Target: ${targetText}` });
    marks.sort((a, b) => a.pos - b.pos);
    const labelPos = spreadLabels(marks.map((m) => m.pos));
    marks = marks.map((m, i) => ({ ...m, labelPos: labelPos[i] }));
  }

  return (
    <div>
      {/* Where you are | where you're going */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-0.5">
            You now
          </p>
          {now !== null && now !== undefined ? (
            <p className="text-2xl font-bold text-solar whitespace-nowrap">
              {formatValue(now, unit)}
            </p>
          ) : (
            <p className="text-base font-medium text-gray-600 dark:text-gray-300">No data yet</p>
          )}
          {start !== null && start !== undefined && now !== null && (
            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
              started {formatShort(start, unit)}
            </p>
          )}
        </div>

        <div className="text-right min-w-0">
          <p className="text-sm font-semibold uppercase tracking-wider text-good-strong dark:text-good mb-0.5">
            Your target
          </p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {targetText || '—'}
          </p>
        </div>
      </div>

      {/* The path */}
      {inBand ? (
        <div className="mt-3 flex items-center gap-1.5 text-green-700 dark:text-green-400">
          <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium">in your target range</span>
        </div>
      ) : marks ? (
        <div className="mt-3">
          {hasNext && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1.5 text-center">
              next step:{' '}
              <span className="pill-next inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-semibold whitespace-nowrap">
                ▲ {formatShort(next, unit)}
                {nextWeeks ? <span className="font-medium opacity-90"> · {nextWeeks} wks</span> : null}
              </span>
            </p>
          )}
          <div className="relative w-full h-10">
            <div className="absolute inset-x-0 top-[8px] border-t-2 border-dashed border-gray-300 dark:border-gray-600" />
            {/* Gradient progress: how far along the journey "now" sits. */}
            {(() => {
              const nowMark = marks.find((m) => m.key === 'now');
              return nowMark ? (
                <div
                  className="absolute top-[7px] h-[3px] rounded-full bg-solar-gradient solar-glow"
                  style={{ left: '2%', width: `calc(${dotLeft(nowMark.pos)} - 2%)` }}
                />
              ) : null;
            })()}
            {marks.map((m) => (
              <React.Fragment key={m.key}>
                <div
                  className={`absolute top-[9px] -translate-y-1/2 -translate-x-1/2 ${markStyle[m.key]}`}
                  style={{ left: dotLeft(m.pos) }}
                  title={m.title}
                />
                <span
                  className={`absolute top-[20px] -translate-x-1/2 text-sm ${labelStyle[m.key]}`}
                  style={{ left: dotLeft(m.labelPos) }}
                  title={m.title}
                >
                  {m.label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default JourneyTrack;
